import { createAsyncThunk } from '@reduxjs/toolkit';
import { uploadAvatar, deleteAvatar } from '../../../api/avatarApi';
import { type IUser } from '../../../types/IUser';
import {
  CANCEL_CODE,
  getErrorData,
  type IErrorData
} from '../../../utils/getErrorData';

let uploadAbortController: AbortController | null = null;
let deleteAbortController: AbortController | null = null;

/**
 * Thunk для загрузки аватара пользователя
 */
export const uploadAvatarThunk = createAsyncThunk<
  IUser,
  { userUuid: string; file: File },
  { rejectValue: IErrorData | undefined }
>('account/uploadAvatar', async ({ userUuid, file }, { rejectWithValue }) => {
  try {
    uploadAbortController?.abort();

    uploadAbortController = new AbortController();

    const result = await uploadAvatar(userUuid, file);

    if (result === undefined) {
      return rejectWithValue({ message: 'Не удалось загрузить аватар' });
    }

    if (result.meta.error !== null) {
      const errorMessage =
        typeof result.meta.error === 'string'
          ? result.meta.error
          : result.meta.error.message;
      return rejectWithValue({ message: errorMessage });
    }

    if (result.response === null) {
      return rejectWithValue({ message: 'Некорректный ответ сервера' });
    }

    return result.response;
  } catch (error: unknown) {
    const extractedError = getErrorData(error);

    if (extractedError.code === CANCEL_CODE) {
      return rejectWithValue(undefined);
    }

    return rejectWithValue(extractedError);
  } finally {
    uploadAbortController = null;
  }
});

/**
 * Thunk для удаления аватара пользователя
 */
export const deleteAvatarThunk = createAsyncThunk<
  IUser,
  string,
  { rejectValue: IErrorData | undefined }
>('account/deleteAvatar', async (userUuid, { rejectWithValue }) => {
  try {
    deleteAbortController?.abort();

    deleteAbortController = new AbortController();

    const result = await deleteAvatar(userUuid);

    if (result === undefined) {
      return rejectWithValue({ message: 'Не удалось удалить аватар' });
    }

    if (result.meta.error !== null) {
      const errorMessage =
        typeof result.meta.error === 'string'
          ? result.meta.error
          : result.meta.error.message;
      return rejectWithValue({ message: errorMessage });
    }

    if (result.response === null) {
      return rejectWithValue({ message: 'Некорректный ответ сервера' });
    }

    return result.response;
  } catch (error: unknown) {
    const extractedError = getErrorData(error);

    if (extractedError.code === CANCEL_CODE) {
      return rejectWithValue(undefined);
    }

    return rejectWithValue(extractedError);
  } finally {
    deleteAbortController = null;
  }
});

/**
 * Отмена запроса загрузки аватара
 */
export const cancelUploadAvatarRequest = (): void => {
  if (uploadAbortController !== null) {
    uploadAbortController.abort();
    uploadAbortController = null;
  }
};

/**
 * Отмена запроса удаления аватара
 */
export const cancelDeleteAvatarRequest = (): void => {
  if (deleteAbortController !== null) {
    deleteAbortController.abort();
    deleteAbortController = null;
  }
};
